import { API, postJSON } from "../../api.js";
import { $, esc, fmtMoney } from "../../ui/format.js";
import { helpIcon } from "../../ui/help.js";
import { toast } from "../../ui/modals.js";

// ---------------------------------------------------------------- Risk & Compliance Engine

const INSURANCE_DEFAULT_COVERAGE = {
  "Cyber Liability": 500000,
  "General Liability": 400000,
  "Property & Business Interruption": 300000,
  "Political Risk": 250000,
};

function _riskProbClass(pct) {
  if (pct >= 3) return "high";
  if (pct >= 1) return "med";
  return "low";
}

async function renderRisk(main, snap) {
  main.innerHTML = `
    <h2 class="panel-title">Risk &amp; Compliance — Month ${snap.month} ${helpIcon("risk")}</h2>
    <div class="plain-english-card" id="risk-plain-english">Loading…</div>
    <div class="risk-stat-row" id="risk-stats">Loading…</div>
    <div class="deal-actions" style="margin-bottom:22px;">
      <button id="btn-compliance-audit">Run Compliance Audit ($100K)</button>
      <button id="btn-cyber-invest">Invest in Cyber Defense ($80K)</button>
    </div>
    <h3 style="font-family:var(--font-display); font-size:17px; margin:8px 0 12px;">Risk Register</h3>
    <div id="risk-register">Loading…</div>
    <h3 style="font-family:var(--font-display); font-size:17px; margin:24px 0 12px;">Insurance Policies</h3>
    <div id="insurance-policies">Loading…</div>
    <h3 style="font-family:var(--font-display); font-size:17px; margin:24px 0 12px;">Recent Incidents</h3>
    <div id="risk-incidents">Loading…</div>
  `;

  const [reg, policies, incidents] = await Promise.all([
    fetch(`${API}/risk/register`).then((x) => x.json()),
    fetch(`${API}/risk/insurance`).then((x) => x.json()),
    fetch(`${API}/risk/incidents`).then((x) => x.json()),
  ]);

  const biggestUninsured = [...reg.register].filter((r) => !r.insured).sort((a, b) => b.monthly_probability_pct - a.monthly_probability_pct)[0];
  $("risk-plain-english").innerHTML = biggestUninsured
    ? `Your biggest exposure right now is <strong style="color:var(--oxblood);">${esc(biggestUninsured.label)}</strong>, uninsured at
       ${biggestUninsured.monthly_probability_pct}% odds per month. ${policies.length ? `You have ${policies.length} other polic${policies.length === 1 ? "y" : "ies"} in place.` : "You currently have no active insurance at all."}
       Compliance ${reg.compliance_score}/100, Cyber Defense ${reg.cyber_defense_level}/100 — both decay if you don't reinvest.`
    : `Every meaningful risk is currently insured. Compliance ${reg.compliance_score}/100, Cyber Defense ${reg.cyber_defense_level}/100 — both decay if you don't reinvest.`;

  $("risk-stats").innerHTML = `
    <div class="risk-stat-box">
      <div class="risk-stat-label">Compliance Score</div>
      <div class="risk-stat-value">${reg.compliance_score}<span style="font-size:14px;color:var(--text-low)">/100</span></div>
    </div>
    <div class="risk-stat-box">
      <div class="risk-stat-label">Cyber Defense Level</div>
      <div class="risk-stat-value">${reg.cyber_defense_level}<span style="font-size:14px;color:var(--text-low)">/100</span></div>
    </div>
    <div class="risk-stat-box">
      <div class="risk-stat-label">Active Policies</div>
      <div class="risk-stat-value">${policies.length}<span style="font-size:14px;color:var(--text-low)">/4</span></div>
    </div>
  `;

  $("btn-compliance-audit").addEventListener("click", async () => {
    const res = await postJSON("/risk/compliance-audit", { amount: 100000 });
    await window.refreshAll();
    toast(res.message);
  });
  $("btn-cyber-invest").addEventListener("click", async () => {
    const res = await postJSON("/risk/invest-cyber-defense", { amount: 80000 });
    await window.refreshAll();
    toast(res.message);
  });

  $("risk-register").innerHTML = reg.register.map((r) => `
    <div class="risk-row">
      <div class="risk-row-left">
        <div class="risk-row-label">${esc(r.label)}</div>
        <div class="risk-row-sub">Mitigated by ${esc(r.mitigated_by)} (${r.mitigation_value}) · covered by ${esc(r.insurance_policy)}</div>
      </div>
      <div style="display:flex; align-items:center; gap:12px;">
        <span class="risk-prob ${_riskProbClass(r.monthly_probability_pct)}">${r.monthly_probability_pct}%/mo</span>
        <span class="${r.insured ? "insured-tag" : "uninsured-tag"}">${r.insured ? "Insured" : "Uninsured"}</span>
        ${r.insured ? "" : `<button data-buy="${esc(r.insurance_policy)}">Buy Coverage</button>`}
      </div>
    </div>
  `).join("");

  $("risk-register").querySelectorAll("[data-buy]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const policyType = btn.dataset.buy;
      const res = await postJSON("/risk/insurance/buy", {
        policy_type: policyType,
        coverage_limit: INSURANCE_DEFAULT_COVERAGE[policyType] || 300000,
      });
      await window.refreshAll();
      toast(res.message);
    });
  });

  $("insurance-policies").innerHTML = policies.length ? policies.map((p) => `
    <div class="risk-row">
      <div class="risk-row-left">
        <div class="risk-row-label">${esc(p.policy_type)}</div>
        <div class="risk-row-sub">Coverage ${fmtMoney(p.coverage_limit)} · ${fmtMoney(p.monthly_premium)}/mo premium · bound month ${p.purchased_month}</div>
      </div>
      <button data-cancel="${esc(p.policy_type)}">Cancel</button>
    </div>
  `).join("") : `<p style="color:var(--text-mid)">No active coverage — you're self-insuring every incident type.</p>`;

  $("insurance-policies").querySelectorAll("[data-cancel]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const res = await postJSON("/risk/insurance/cancel", { policy_type: btn.dataset.cancel });
      await window.refreshAll();
      toast(res.message);
    });
  });

  $("risk-incidents").innerHTML = incidents.length ? incidents.map((i) => `
    <div class="incident-card">
      <strong>M${i.month} — ${esc(i.description)}</strong><br/>
      Gross ${fmtMoney(i.gross_cost)} · Insurance covered ${fmtMoney(i.insurance_covered)} · Net cost ${fmtMoney(i.net_cost)} · Reputation ${i.reputation_impact > 0 ? "+" : ""}${i.reputation_impact}
    </div>
  `).join("") : `<p style="color:var(--text-mid)">No incidents yet.</p>`;
}

export { renderRisk };
